import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ComicCard from "@/components/base/ComicCard";
import SectionHeading from "@/components/base/SectionHeading";
import { comics, categories, rankings, comicById } from "@/mocks/comics";

export default function Ranking() {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");
  const [cat, setCat] = useState(searchParams.get("cat") || "全部");

  const hasFilter = Boolean(keyword.trim()) || cat !== "全部";

  const filtered = useMemo(() => {
    let list = cat === "全部" ? comics : comics.filter((c) => c.category === cat);
    if (keyword.trim()) {
      const k = keyword.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(k) ||
          c.author.toLowerCase().includes(k) ||
          c.tags.some((t) => t.toLowerCase().includes(k)),
      );
    }
    return list;
  }, [cat, keyword]);

  const rankMap = useMemo(() => {
    const map: Record<string, number> = {};
    rankings.forEach((r) => {
      map[r.comicId] = r.rank;
    });
    return map;
  }, []);

  const rankingComics = rankings
    .map((r) => ({ rank: r, comic: comicById(r.comicId) }))
    .filter((x) => x.comic);

  return (
    <div>
      <section className="bg-background-100 py-8">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">漫画排行</span>
          </nav>
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">漫画排行与搜索</h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                按人气热度查看本周追更榜单，也可以通过关键词或分类，快速找到想看的漫画。
              </p>
            </div>
            <div className="relative w-full lg:w-80">
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 作者 / 标签"
                className="h-11 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition ${
                  cat === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {hasFilter ? (
          <div>
            <SectionHeading
              eyebrow="筛选结果"
              title={keyword.trim() ? `“${keyword.trim()}” 的搜索结果` : `${cat}漫画`}
              desc={`共找到 ${filtered.length} 部作品。`}
            />
            {filtered.length > 0 ? (
              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-6">
                {filtered.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
                <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的漫画</p>
                <p className="mt-1 text-xs text-foreground-400">换个关键词或分类试试</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <SectionHeading
                eyebrow="实时人气"
                title="本周人气排行榜"
                desc="根据近 7 天阅读热度与订阅数综合排名，看看大家都在追什么。"
              />
            </div>

            <ol className="mt-8 flex flex-col gap-3">
              {rankingComics.map(({ rank, comic }) => {
                if (!comic) return null;
                const isTop3 = rank.rank <= 3;
                return (
                  <li
                    key={comic.id}
                    className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition hover:border-primary-300"
                  >
                    <span
                      className={`w-9 shrink-0 text-center font-heading text-2xl ${
                        rank.rank === 1
                          ? "text-primary-500"
                          : rank.rank === 2
                            ? "text-secondary-500"
                            : rank.rank === 3
                              ? "text-accent-600"
                              : "text-foreground-300"
                      }`}
                    >
                      {rank.rank}
                    </span>
                    <div className="hidden h-16 w-12 shrink-0 overflow-hidden rounded bg-background-200 sm:block">
                      <img
                        src={comic.cover}
                        alt={`${comic.name}封面`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="truncate text-sm font-bold text-foreground-950">
                          {comic.name}
                        </h2>
                        {isTop3 && (
                          <i
                            className={`${
                              rank.rank === 1
                                ? "ri-fire-fill text-primary-500"
                                : "ri-star-fill text-secondary-500"
                            }`}
                          ></i>
                        )}
                      </div>
                      <p className="mt-1 truncate text-xs text-foreground-500">
                        {comic.author} · {comic.category} · {comic.chapters}
                      </p>
                    </div>
                    <div className="hidden items-center gap-1 text-xs text-foreground-500 md:flex">
                      <i className="ri-eye-line"></i> {rank.weekly}
                    </div>
                    <div className="hidden items-center gap-1 text-xs text-foreground-500 md:flex">
                      <i className="ri-star-fill text-secondary-500"></i> {comic.rating}
                    </div>
                    <span
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-sm ${
                        rank.trend === "up"
                          ? "bg-primary-100 text-primary-700"
                          : rank.trend === "down"
                            ? "bg-background-100 text-foreground-400"
                            : "bg-secondary-100 text-secondary-700"
                      }`}
                    >
                      <i
                        className={`${
                          rank.trend === "up"
                            ? "ri-arrow-up-line"
                            : rank.trend === "down"
                              ? "ri-arrow-down-line"
                              : "ri-subtract-line"
                        }`}
                      ></i>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </section>
    </div>
  );
}